/**
 * @file        src/lib/pdf.js
 * @description Turn an on-screen (or off-screen) DOM node into a paginated A4
 *              PDF — jsPDF + html2canvas, ported 1:1 from the customer site's
 *              `booking/booking_frontends/neon/src/lib/pdf.js` (`downloadElementAsPdf`,
 *              same pagination math), but returning the PDF as base64 instead of
 *              triggering a browser download. Used by ShareEmailModal (CR D2) to
 *              build the Ticket/Invoice/Voucher attachment(s) for the "Share by
 *              email" action — the admin backend does no PDF generation of its
 *              own, this is the only PDF producer.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-09-09 Claude — JPEG quality 0.95 → 0.82. Share by Email posts these
 *                          PDFs as base64 in the request body, and at 0.95 a
 *                          single ticket's page image was ~852KB → ~1.11MB
 *                          base64, so even ONE attachment cleared nginx's 1MB
 *                          default client_max_body_size and "Both" (two docs)
 *                          more than doubled it — a 413 before Express (limit
 *                          15mb) ever saw the request. 0.82 is ~3x smaller with
 *                          no visible loss on a text document. This is the one
 *                          deliberate divergence from the customer site's
 *                          pdf.js, which downloads locally and has no body
 *                          limit to clear. The proxy limit still needs raising
 *                          to 15m to match Express — this is margin, not the fix.
 *   - 2026-08-04 Claude — Initial (CR D2), ported from the customer site's pdf.js.
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Render `el` to a multi-page A4 PDF and return it as a base64 string (no
 * `data:` prefix) — ready to hand straight to nodemailer's
 * `attachments: [{ filename, content, encoding: 'base64' }]`.
 * @param {HTMLElement} el  the node to capture (must be visible/rendered — NOT
 *                          `display:none`; `position:fixed;left:-9999px` is fine,
 *                          html2canvas cannot rasterize a display:none element)
 * @returns {Promise<string>} base64-encoded PDF bytes
 */
export async function elementToPdfBase64(el) {
  if (!el) throw new Error('Nothing to export.');

  // As the customer site's downloadElementAsPdf: scale:2 keeps text crisp, a
  // forced white background so a transparent corner never prints black, useCORS
  // for any cross-origin images (airline logos). Capture scale is deliberately
  // NOT reduced — softening the text is far more visible than the JPEG quality
  // drop below, and costs less size than re-encoding does.
  const canvas = await html2canvas(el, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    windowWidth: el.scrollWidth,
  });

  // 0.82, not the customer site's 0.95: this PDF travels as base64 inside a
  // JSON request body (Share by Email), so its byte size has to clear a proxy
  // body limit that a local download never touches. See @changelog.
  const img = canvas.toDataURL('image/jpeg', 0.82);
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // Fit the capture to the page width; walk it down page by page if taller than
  // one page (identical pagination logic to the customer site's function).
  const imgW = pageW;
  const imgH = (canvas.height * imgW) / canvas.width;
  let heightLeft = imgH;
  let position = 0;

  pdf.addImage(img, 'JPEG', 0, position, imgW, imgH);
  heightLeft -= pageH;
  while (heightLeft > 0) {
    position -= pageH;
    pdf.addPage();
    pdf.addImage(img, 'JPEG', 0, position, imgW, imgH);
    heightLeft -= pageH;
  }

  const dataUri = pdf.output('datauristring');
  const base64 = dataUri.split(',')[1] || '';
  if (!base64) throw new Error('PDF generation produced no data.');
  return base64;
}
